import { Capabilities } from './behavior';
import { mapAcquisitionError } from './mapAcquisitionError';
import { DeviceKind, DeviceOutcome } from './types';

/**
 * Browser surface the core depends on, plus the engine capabilities that change how a failure is
 * classified. Injectable so the acquisition logic can be unit-tested without a real browser (and
 * so it degrades gracefully when the APIs are absent, e.g. insecure contexts).
 */
export type AcquireDeps = {
  mediaDevices: Pick<MediaDevices, 'getUserMedia' | 'enumerateDevices'> | undefined;
  permissions: Pick<Permissions, 'query'> | undefined;
  /**
   * Engine behaviour that classification depends on. Omitted here rather than defaulted, because
   * deriving it means reading the UA and `core` stays global-free — the `devices/state` thunks
   * (the only production callers) supply it from `getCapabilities()`. When absent, classification
   * keeps the permissive Chrome/Edge/Firefox reading.
   */
  capabilities?: Pick<Capabilities, 'reportsPermissionDenial' | 'permissionSemantics'>;
};

/**
 * The live browser surface, plus whatever engine capabilities the caller has resolved. Callers in
 * `devices/state` pass `getCapabilities()`; `core` never reads the UA itself.
 */
export const defaultAcquireDeps = (capabilities?: AcquireDeps['capabilities']): AcquireDeps => ({
  mediaDevices: typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined,
  permissions: typeof navigator !== 'undefined' ? navigator.permissions : undefined,
  capabilities,
});

const defaultDeps = (): AcquireDeps => defaultAcquireDeps();

const mediaKindFor = (kind: DeviceKind): MediaDeviceKind => (kind === 'microphone' ? 'audioinput' : 'videoinput');

/** Non-prompting: is at least one input of this kind listed (labels may be empty pre-grant). */
export const isDeviceKindEnumerated = async (kind: DeviceKind, deps: AcquireDeps = defaultDeps()): Promise<boolean> => {
  const { mediaDevices } = deps;
  if (!mediaDevices?.enumerateDevices) return false;
  try {
    const devices = await mediaDevices.enumerateDevices();
    const target = mediaKindFor(kind);
    return devices.some((device) => device.kind === target);
  } catch {
    return false;
  }
};

const constraintsFor = (kind: DeviceKind): MediaStreamConstraints =>
  kind === 'microphone' ? { audio: true, video: false } : { audio: false, video: true };

const combinedConstraints = (kinds: DeviceKind[]): MediaStreamConstraints => ({
  audio: kinds.includes('microphone'),
  video: kinds.includes('camera'),
});

/**
 * A wholesale `getUserMedia` rejection that means "a requested device kind is absent"
 * rather than "permission refused". A combined request can't attribute this to a kind,
 * so it's the signal to fall back to per-kind probing. (`DevicesNotFoundError` is the
 * legacy name some browsers still use.)
 */
const isMissingDeviceError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError');

const disposeStream = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // Already stopped — nothing to do.
    }
  }
};

const ACQUIRE_TIMEOUT_MS = 10_000;

class AcquireTimeoutError extends Error {
  override name = 'AcquireTimeoutError';
  constructor() {
    super('getUserMedia did not settle within the allowed time');
  }
}

/**
 * Race getUserMedia against a timeout. Safari hangs indefinitely when no device hardware
 * is present (e.g. laptop lid closed), never resolving or rejecting — this converts the
 * hang into a catchable error so the acquisition settles and the UI stays responsive.
 */
const getUserMediaWithTimeout = async (
  mediaDevices: Pick<MediaDevices, 'getUserMedia'>,
  constraints: MediaStreamConstraints
): Promise<MediaStream> => {
  const gumPromise = mediaDevices.getUserMedia(constraints);
  let timerId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new AcquireTimeoutError()), ACQUIRE_TIMEOUT_MS);
  });

  try {
    const stream = await Promise.race([gumPromise, timeoutPromise]);
    clearTimeout(timerId!);
    return stream;
  } catch (error) {
    clearTimeout(timerId!);
    gumPromise.then(disposeStream).catch(() => {});
    throw error;
  }
};

/**
 * Non-prompting read of the browser permission for a device kind. Returns `null`
 * when the Permissions API is unavailable or the kind isn't queryable.
 */
export const queryBrowserPermission = async (
  kind: DeviceKind,
  deps: AcquireDeps = defaultDeps()
): Promise<PermissionState | null> => {
  const { permissions } = deps;
  if (!permissions?.query) return null;
  try {
    const status = await permissions.query({ name: kind as PermissionName });
    return status.state;
  } catch {
    // Some browsers throw for 'camera'/'microphone' names — treat as unknown.
    return null;
  }
};

/**
 * Acquire a single device kind and resolve to a {@link DeviceOutcome}. Never throws.
 *
 * This is a *probe*: on success the stream is immediately disposed (mic/camera LED
 * goes dark). Its job is to establish permission/availability and classify the
 * result — the real publishing track is created separately by the Agora layer once
 * permission is held. Because permission is cached after the first grant, the later
 * Agora acquisition does not trigger a second prompt.
 *
 * MUST be invoked synchronously from a user gesture (click) for Safari, which only
 * honours `getUserMedia` under transient activation.
 */
export const acquireDevice = async (kind: DeviceKind, deps: AcquireDeps = defaultDeps()): Promise<DeviceOutcome> => {
  const { mediaDevices } = deps;
  if (!mediaDevices?.getUserMedia) return DeviceOutcome.Unsupported;

  try {
    const stream = await getUserMediaWithTimeout(mediaDevices, constraintsFor(kind));
    disposeStream(stream);
    return DeviceOutcome.Granted;
  } catch (error) {
    const browserPermissionAfter = await queryBrowserPermission(kind, deps);
    const deviceKindPresent = isMissingDeviceError(error) ? await isDeviceKindEnumerated(kind, deps) : false;
    return mapAcquisitionError(error, browserPermissionAfter, {
      deviceKindPresent,
      reportsPermissionDenial: deps.capabilities?.reportsPermissionDenial,
      permissionSemantics: deps.capabilities?.permissionSemantics,
    });
  }
};

/**
 * Acquire several kinds and return a per-kind outcome map.
 *
 * The kinds are requested in a *single* `getUserMedia` call so the browser shows one
 * merged permission prompt (asking once for camera + microphone), matching the
 * pre-refactor UX, rather than a separate prompt per kind.
 *
 * The catch is that a combined request fails wholesale (`NotFoundError`) if *either*
 * device is missing — which would leave a mic-only desktop stuck. So on a missing-device
 * failure we fall back to acquiring each kind independently, which reports a working
 * microphone as `Granted` even with no camera. A missing kind rejects *without*
 * prompting, so the fallback adds no extra prompt in the common single-missing case; and
 * it is *sequential* (Safari can spuriously reject one of two concurrent `getUserMedia`
 * calls from the same frame). Any other failure (denied/dismissed/system-denied) was
 * already settled for every kind by the one prompt, so it's mapped across them directly.
 */
export const acquireDevices = async (
  request: Partial<Record<DeviceKind, boolean>>,
  deps: AcquireDeps = defaultDeps()
): Promise<Partial<Record<DeviceKind, DeviceOutcome>>> => {
  const kinds = (Object.keys(request) as DeviceKind[]).filter((kind) => request[kind]);
  if (kinds.length === 0) return {};
  if (kinds.length === 1) return { [kinds[0]]: await acquireDevice(kinds[0], deps) };

  const { mediaDevices } = deps;
  if (!mediaDevices?.getUserMedia) {
    return Object.fromEntries(kinds.map((kind) => [kind, DeviceOutcome.Unsupported]));
  }

  try {
    const stream = await getUserMediaWithTimeout(mediaDevices, combinedConstraints(kinds));
    disposeStream(stream);
    return Object.fromEntries(kinds.map((kind) => [kind, DeviceOutcome.Granted]));
  } catch (error) {
    if (isMissingDeviceError(error)) {
      const results: Partial<Record<DeviceKind, DeviceOutcome>> = {};
      for (const kind of kinds) {
        results[kind] = await acquireDevice(kind, deps);
      }
      return results;
    }

    const results: Partial<Record<DeviceKind, DeviceOutcome>> = {};
    for (const kind of kinds) {
      results[kind] = mapAcquisitionError(error, await queryBrowserPermission(kind, deps), {
        reportsPermissionDenial: deps.capabilities?.reportsPermissionDenial,
        permissionSemantics: deps.capabilities?.permissionSemantics,
      });
    }
    return results;
  }
};
